import React, { useEffect } from 'react';
import FormInput from './FormInput';
import FormTextArea from './FormTextArea';
import FormRadio from './FormRadio';
import FormCheckbox from './FormCheckbox';
import FormSelect from './FormSelect';
import SearchableDiagnosisDropdown from './SearchableDiagnosisDropdown';
import { PRP_ADULTS_DIAGNOSES } from './diagnosisData';
import { calculateAge, US_STATES, validateZipCode, formatSSN } from './formUtils';
import {
  YES_NO, YES_NO_PENDING, YES_NO_REVIEW, SEX_OPTIONS, GENDER_OPTIONS,
  PRONOUN_OPTIONS, INSURANCE_TYPES, LANGUAGE_OPTIONS, RACE_OPTIONS,
  ETHNICITY_OPTIONS, MARITAL_OPTIONS, LIVING_OPTIONS, ADULT_EMPLOYMENT,
  MINOR_SCHOOL, MINOR_NEEDS
} from './formConstants';

const FUNCTIONAL_CRITERIA_OPTIONS = [
  { key: 'employment', label: 'Marked inability to establish or maintain competitive employment' },
  { key: 'dailyLiving', label: 'Marked inability to perform instrumental activities of daily living' },
  { key: 'supportSystem', label: 'Marked inability to establish/maintain a personal support system' },
  { key: 'concentration', label: 'Deficiencies in concentration / persistence / pace' },
  { key: 'selfDirection', label: 'Marked deficiencies in self-direction' },
  { key: 'financialAssistance', label: 'Marked inability to procure financial assistance' }
];

const PRPReferralForm = ({ formData, onDataUpdate, validationErrors }) => {
  const clientType = formData.clientType || 'Adult';
  const prpData = formData.prp || {};
  const errors = validationErrors || {};

  const prpDiagnosisList = clientType === 'Adult' ? PRP_ADULTS_DIAGNOSES : null;

  const handleChange = (field, value) => {
    onDataUpdate({ prp: { ...prpData, [field]: value } });
  };

  const handleMultiChange = (updates) => {
    onDataUpdate({ prp: { ...prpData, ...updates } });
  };

  const handleCheckboxChange = (field, value) => {
    const current = prpData[field] || [];
    const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    handleChange(field, updated);
  };

  const handleFunctionalCriteriaToggle = (key) => {
    const current = prpData.functionalCriteria || {};
    if (current[key]) {
      const updated = { ...current };
      delete updated[key];
      handleChange('functionalCriteria', updated);
    } else {
      handleChange('functionalCriteria', { ...current, [key]: { selected: true, evidence: '' } });
    }
  };

  const handleFunctionalEvidenceChange = (key, evidence) => {
    const current = prpData.functionalCriteria || {};
    handleChange('functionalCriteria', { ...current, [key]: { ...current[key], evidence } });
  };

  const selectedCriteriaCount = Object.keys(prpData.functionalCriteria || {}).length;

  useEffect(() => {
    if (prpData.dob) {
      const age = calculateAge(prpData.dob);
      if (age !== prpData.age) handleChange('age', age);
    }
  }, [prpData.dob]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-teal-100 p-4 rounded-lg border-l-4 border-green-600">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PRP Referral Form</h1>
            <p className="text-sm text-gray-600 mt-1">Psychiatric Rehabilitation Program</p>
          </div>
          <div className="bg-green-600 text-white px-4 py-2 rounded-full font-bold text-sm">
            Client Type: {clientType}
          </div>
        </div>
      </div>

      {errors.prpRequired && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="prp-required-errors">
          Please complete all required fields before proceeding.
        </div>
      )}

      {/* ─── PRP Referral Information ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">PRP Referral Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <FormInput label="Last Name" value={prpData.lastName} onChange={(v) => handleChange('lastName', v)} required error={errors.prpLastName} />
          <FormInput label="First Name" value={prpData.firstName} onChange={(v) => handleChange('firstName', v)} required error={errors.prpFirstName} />
          <FormInput label="Middle Name" value={prpData.middleName} onChange={(v) => handleChange('middleName', v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Date of Birth" type="date" value={prpData.dob} onChange={(v) => handleChange('dob', v)} required error={errors.prpDob} />
          <FormInput label="Age" type="number" value={prpData.age} onChange={(v) => handleChange('age', v)} disabled helperText="Auto-calculated from Date of Birth" />
          <FormInput label="Referral Date" type="date" value={prpData.referralDate} onChange={(v) => handleChange('referralDate', v)} required />
        </div>
        <div className="mt-4 space-y-4">
          <FormRadio label="Current Outpatient Mental Health Treatment?" name="prpCurrentOMHC" options={YES_NO} value={prpData.currentOMHC} onChange={(v) => handleChange('currentOMHC', v)} required />
          {errors.prpCurrentOMHC && <p className="text-xs text-red-600">{errors.prpCurrentOMHC}</p>}
          <FormRadio label="Referring Provider is Current Treating Clinician?" name="prpCurrentTreatingClinician" options={YES_NO} value={prpData.currentTreatingClinician} onChange={(v) => handleChange('currentTreatingClinician', v)} />
          <FormRadio label="Prior PRP History?" name="prpPriorPRP" options={YES_NO} value={prpData.priorPRP} onChange={(v) => handleChange('priorPRP', v)} />
          {prpData.priorPRP === 'Yes' && (
            <FormInput label="Prior PRP Provider" value={prpData.priorPRPProvider} onChange={(v) => handleChange('priorPRPProvider', v)} />
          )}
        </div>
      </section>

      {/* ─── Client Demographics (mirrored from OMHC) ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Client Demographics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Preferred Name" value={prpData.preferredName} onChange={(v) => handleChange('preferredName', v)} />
          <FormSelect label="Sex Assigned at Birth" value={prpData.sexAssigned} onChange={(v) => handleChange('sexAssigned', v)} options={SEX_OPTIONS} />
          <FormSelect label="Gender Identity" value={prpData.genderIdentity} onChange={(v) => handleChange('genderIdentity', v)} options={GENDER_OPTIONS} />
          <FormSelect label="Pronouns" value={prpData.pronouns} onChange={(v) => handleChange('pronouns', v)} options={PRONOUN_OPTIONS} />
          {prpData.pronouns === 'Other (please specify)' && (
            <FormInput label="Other Pronouns" value={prpData.otherPronouns} onChange={(v) => handleChange('otherPronouns', v)} />
          )}
        </div>
      </section>

      {/* ─── Insurance & Identification ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Insurance & Identification</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="SSN" value={prpData.ssn} onChange={(v) => handleChange('ssn', formatSSN(v))} placeholder="XXX-XX-XXXX" helperText="Format: 123-45-6789 (9 digits)" />
        </div>
        <div className="mt-4">
          <FormSelect label="Insurance Type" value={prpData.insuranceType} onChange={(v) => handleChange('insuranceType', v)} options={INSURANCE_TYPES} required />
          {prpData.insuranceType && prpData.insuranceType !== 'Self Pay' && (
            <div className="mt-4">
              <FormInput label={`${prpData.insuranceType} ID Number`} value={prpData.insuranceIdNumber} onChange={(v) => handleChange('insuranceIdNumber', v)} />
            </div>
          )}
        </div>
      </section>

      {/* ─── Contact Information ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Contact Information</h2>
        {(errors.prpHomeAddress || errors.prpHomeCity || errors.prpHomeState || errors.prpHomeZip || errors.prpPhone) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Please complete all required contact fields before proceeding.
          </div>
        )}
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Home Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormInput label="Address Line 1" value={prpData.homeAddressLine1} onChange={(v) => handleChange('homeAddressLine1', v)} required placeholder="Street address" error={errors.prpHomeAddress} />
            </div>
            <div className="md:col-span-2">
              <FormInput label="Address Line 2" value={prpData.homeAddressLine2} onChange={(v) => handleChange('homeAddressLine2', v)} placeholder="Apt, Suite, Unit (optional)" />
            </div>
            <FormInput label="City" value={prpData.homeCity} onChange={(v) => handleChange('homeCity', v)} required error={errors.prpHomeCity} />
            <FormSelect label="State" value={prpData.homeState} onChange={(v) => handleChange('homeState', v)} options={US_STATES} required error={errors.prpHomeState} />
            <FormInput label="Zip Code" value={prpData.homeZip} onChange={(v) => handleChange('homeZip', v)} required placeholder="e.g., 21208" error={errors.prpHomeZip || (prpData.homeZip ? validateZipCode(prpData.homeZip) : '')} />
          </div>
          <div className="mt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={prpData.mailingAddressDifferent || false} onChange={(e) => handleChange('mailingAddressDifferent', e.target.checked)} className="w-4 h-4 text-green-600 focus:ring-2 focus:ring-green-500 rounded" />
              <span className="text-sm font-medium text-gray-700">Mailing address is different from home address</span>
            </label>
          </div>
          {prpData.mailingAddressDifferent && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Mailing Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2"><FormInput label="Address Line 1" value={prpData.mailAddressLine1} onChange={(v) => handleChange('mailAddressLine1', v)} required placeholder="Street address" /></div>
                <div className="md:col-span-2"><FormInput label="Address Line 2" value={prpData.mailAddressLine2} onChange={(v) => handleChange('mailAddressLine2', v)} placeholder="Apt, Suite, Unit (optional)" /></div>
                <FormInput label="City" value={prpData.mailCity} onChange={(v) => handleChange('mailCity', v)} required />
                <FormSelect label="State" value={prpData.mailState} onChange={(v) => handleChange('mailState', v)} options={US_STATES} required />
                <FormInput label="Zip Code" value={prpData.mailZip} onChange={(v) => handleChange('mailZip', v)} required placeholder="e.g., 21208" error={prpData.mailZip ? validateZipCode(prpData.mailZip) : ''} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput label="Phone" type="tel" value={prpData.clientPhone} onChange={(v) => handleChange('clientPhone', v)} required error={errors.prpPhone} />
            <FormInput label="Email" type="email" value={prpData.clientEmail} onChange={(v) => handleChange('clientEmail', v)} />
            <FormSelect label="Preferred Language" value={prpData.preferredLanguage} onChange={(v) => handleChange('preferredLanguage', v)} options={LANGUAGE_OPTIONS} />
            <FormRadio label="Need Interpreter?" name="prpNeedInterpreter" options={YES_NO} value={prpData.needInterpreter} onChange={(v) => handleChange('needInterpreter', v)} />
          </div>
        </div>
      </section>

      {/* ─── Additional Demographics ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Additional Demographics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect label="Race" value={prpData.race} onChange={(v) => handleChange('race', v)} options={RACE_OPTIONS} />
          <FormSelect label="Ethnicity" value={prpData.ethnicity} onChange={(v) => handleChange('ethnicity', v)} options={ETHNICITY_OPTIONS} />
          <FormSelect label="Marital Status" value={prpData.maritalStatus} onChange={(v) => handleChange('maritalStatus', v)} options={MARITAL_OPTIONS} />
          <FormSelect label="Living Situation" value={prpData.livingSituation} onChange={(v) => handleChange('livingSituation', v)} options={LIVING_OPTIONS} />
          <FormSelect label={clientType === 'Minor' ? 'School Status' : 'Employment Status'} value={prpData.employmentStatus} onChange={(v) => handleChange('employmentStatus', v)} options={clientType === 'Minor' ? MINOR_SCHOOL : ADULT_EMPLOYMENT} />
        </div>
      </section>

      {/* ─── Emergency Contact ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Emergency Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="Emergency Contact Name" value={prpData.emergencyContactName} onChange={(v) => handleChange('emergencyContactName', v)} />
          <FormInput label="Relationship to Client" value={prpData.emergencyRelationship} onChange={(v) => handleChange('emergencyRelationship', v)} />
          <FormInput label="Emergency Contact Phone" type="tel" value={prpData.emergencyPhone} onChange={(v) => handleChange('emergencyPhone', v)} />
        </div>
      </section>

      {/* ─── Clinical Information ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Clinical Information</h2>
        {(errors.prpDiagnosis || errors.prpSymptoms) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Please complete all required clinical fields before proceeding.
          </div>
        )}
        <div className="space-y-4">
          <SearchableDiagnosisDropdown
            label="Current Diagnosis"
            value={prpData.currentDiagnosis}
            code={prpData.currentDiagnosisCode}
            onSelect={(name, code) => handleMultiChange({ currentDiagnosis: name, currentDiagnosisCode: code })}
            required
            diagnosisList={prpDiagnosisList}
            helperText={clientType === 'Adult' ? "PRP Adults Referral — restricted to approved diagnosis codes only" : "Select a diagnosis — the ICD/DSM code will auto-populate"}
            error={errors.prpDiagnosis}
          />
          <FormTextArea
            label="Current Symptoms & Reason for PRP Referral"
            value={prpData.symptomsAndReason}
            onChange={(v) => handleChange('symptomsAndReason', v)}
            rows={5}
            required
            helperText="Describe current symptoms, functional needs, and why PRP services are recommended"
            error={errors.prpSymptoms}
          />
          />
        </div>
      </section>

      {/* ─── Adult Functional Criteria ─── */}
      {clientType === 'Adult' && (
        <section className="bg-blue-50 p-6 rounded-lg border-2 border-blue-300" data-testid="functional-criteria-section">
          <h2 className="text-xl font-bold text-blue-900 mb-4 border-b-2 border-blue-600 pb-2">Functional Criteria</h2>
          <div className="mb-6">
            <FormRadio label="Does participant have a new onset (within past 6 months) diagnosis?" name="prpNewOnsetDiagnosis" options={YES_NO} value={prpData.newOnsetDiagnosis} onChange={(v) => handleChange('newOnsetDiagnosis', v)} required />
          </div>
          <div className="mb-6">
            <FormRadio label="Has participant had impairments related to the Priority Population diagnosis in 3 or more functional areas over the last 2 years?" name="prpImpairmentsOver2Years" options={YES_NO} value={prpData.impairmentsOver2Years} onChange={(v) => handleChange('impairmentsOver2Years', v)} required />
          </div>
          <div className="mt-6">
            <h3 className="text-base font-bold text-blue-900 mb-2">Functional Criteria Checklist <span className="text-red-500">*</span></h3>
            <p className="text-sm text-blue-700 mb-4">Select a minimum of 3 criteria. Each selected option requires detailed evidence.</p>
            {errors.functionalCriteria && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="functional-criteria-error">{errors.functionalCriteria}</div>
            )}
            <div className="space-y-4">
              {FUNCTIONAL_CRITERIA_OPTIONS.map((option) => {
                const isSelected = !!(prpData.functionalCriteria && prpData.functionalCriteria[option.key]);
                const evidence = isSelected ? (prpData.functionalCriteria[option.key].evidence || '') : '';
                const hasEvidenceError = errors[`evidence_${option.key}`];
                return (
                  <div key={option.key} className={`border rounded-lg p-4 transition-colors ${isSelected ? 'border-blue-400 bg-white' : 'border-gray-200 bg-white/50'}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={isSelected} onChange={() => handleFunctionalCriteriaToggle(option.key)} className="w-5 h-5 text-blue-600 focus:ring-2 focus:ring-blue-500 rounded mt-0.5" data-testid={`criteria-${option.key}`} />
                      <span className="text-sm font-medium text-gray-900">{option.label}</span>
                    </label>
                    {isSelected && (
                      <div className="mt-3 ml-8">
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Evidence (symptoms, impairment description, concrete examples) <span className="text-red-500">*</span></label>
                        <textarea value={evidence} onChange={(e) => handleFunctionalEvidenceChange(option.key, e.target.value)} rows={2} className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${hasEvidenceError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`} placeholder="Describe evidence of this functional impairment..." data-testid={`evidence-${option.key}`} />
                        {hasEvidenceError && <p className="text-xs text-red-600 mt-1">{hasEvidenceError}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-sm font-medium">
              <span className={selectedCriteriaCount >= 3 ? 'text-green-600' : 'text-orange-600'}>{selectedCriteriaCount}/3 minimum selected</span>
            </div>
          </div>
        </section>
      )}

      {/* ─── Minor PRP Section ─── */}
      {clientType === 'Minor' && (
        <section className="bg-purple-50 p-6 rounded-lg border-2 border-purple-300">
          <h2 className="text-xl font-bold text-purple-900 mb-4 border-b-2 border-purple-600 pb-2">Minor PRP Functional Need Assessment</h2>
          <div className="space-y-2">
            {MINOR_NEEDS.map((need) => (
              <label key={need} className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={(prpData.minorNeeds || []).includes(need)} onChange={() => handleCheckboxChange('minorNeeds', need)} className="w-4 h-4 text-purple-600 focus:ring-2 focus:ring-purple-500 rounded" />
                <span className="text-sm text-gray-800">{need} concerns</span>
              </label>
            ))}
          </div>
          <div className="mt-4">
            <FormRadio label="Minor PRP Criteria Met?" name="prpMinorPRPCriteria" options={YES_NO_REVIEW} value={prpData.minorPRPCriteria} onChange={(v) => handleChange('minorPRPCriteria', v)} required />
          </div>
        </section>
      )}

      {/* ─── PRP Goals ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">PRP Goals / Rehabilitation Needs</h2>
        <div className="space-y-4">
          <FormTextArea label="Rehabilitation Goals" value={prpData.rehabilitationGoals} onChange={(v) => handleChange('rehabilitationGoals', v)} rows={4} helperText="Describe specific goals for PRP services" />
          <FormTextArea label="Strengths / Supports" value={prpData.strengthsSupports} onChange={(v) => handleChange('strengthsSupports', v)} rows={3} helperText="List client strengths and existing support system" />
          <FormTextArea label="Barriers to Treatment" value={prpData.barriersToTreatment} onChange={(v) => handleChange('barriersToTreatment', v)} rows={3} helperText="Identify potential barriers (transportation, housing, etc.)" />
          <FormTextArea label="Additional Comments" value={prpData.additionalComments} onChange={(v) => handleChange('additionalComments', v)} rows={3} />
        </div>
      </section>

      {/* ─── Current Providers ─── */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-green-600 pb-2">Current Providers / Care Coordination</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput label="PCP Name" value={prpData.pcpName} onChange={(v) => handleChange('pcpName', v)} />
          <FormInput label="PCP Phone" type="tel" value={prpData.pcpPhone} onChange={(v) => handleChange('pcpPhone', v)} />
          <FormInput label="Therapist Name" value={prpData.therapistName} onChange={(v) => handleChange('therapistName', v)} />
          <FormInput label="Therapist Phone" type="tel" value={prpData.therapistPhone} onChange={(v) => handleChange('therapistPhone', v)} />
          <FormInput label="Psychiatrist Name" value={prpData.psychiatristName} onChange={(v) => handleChange('psychiatristName', v)} />
          <FormInput label="Psychiatrist Phone" type="tel" value={prpData.psychiatristPhone} onChange={(v) => handleChange('psychiatristPhone', v)} />
          <FormInput label="Case Manager / PRP Worker" value={prpData.caseManager} onChange={(v) => handleChange('caseManager', v)} />
          {clientType === 'Minor' && <FormInput label="School Contact" value={prpData.schoolContact} onChange={(v) => handleChange('schoolContact', v)} />}
        </div>
        <div className="mt-4 space-y-4">
          <FormRadio label="DCS / DSS / Juvenile Services Involvement?" name="prpDcsInvolvement" options={YES_NO} value={prpData.dcsInvolvement} onChange={(v) => handleChange('dcsInvolvement', v)} />
          <FormRadio label="Release of Information on File?" name="prpRoiOnFile" options={YES_NO_PENDING} value={prpData.roiOnFile} onChange={(v) => handleChange('roiOnFile', v)} />
        </div>
      </section>
    </div>
  );
};

export default PRPReferralForm;
